import { Head } from "@inertiajs/react"
import { Flex, Heading, Text } from "@radix-ui/themes"
import { z } from "zod"

import CopyableCode from "@/components/copyable-code"

import ProjectLayout, { type BreadcrumbItem } from "@/layouts/project-layout"
import { projectDashboardPath } from "@/routes"
import { ProjectSchema } from "@/schemas"

const PropsSchema = z.object({
  project: ProjectSchema,
  current_domain: z.string(),
  current_scheme: z.string(),
})

type Props = z.infer<typeof PropsSchema>

export default function DocsShow(props: Props) {
  const validatedProps = PropsSchema.parse(props)
  const { project } = validatedProps

  const exampleEventPayload = `{
  "email": "user@example.com",
  "name": "page_view",
  "custom_fields": {
    "page": "/dashboard",
    "utm_source": "email",
    "utm_campaign": "newsletter"
  }
}`

  const exampleCustomerPayload = `{
  "custom_fields": {
    "age": "30",
    "subscription": "premium",
    "location": "Berlin"
  }
}`

  const exampleDevicePayload = `{
  "email": "user@example.com",
  "apns_token": "apns_device_token_here"
}`

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: project.name,
      href: projectDashboardPath(project.slug),
    },
    {
      title: "Documentation",
    },
  ]

  return (
    <ProjectLayout projectId={project.slug} breadcrumbs={breadcrumbs}>
      <Head title={`${project.name} - Documentation`} />
      <Flex direction="column" gap="6" p="6" height="100%">
        <Heading size="6" weight="medium">
          How to use the API
        </Heading>

        <Text size="2" color="gray">
          Use the Spinus API to track events and update customer data.
        </Text>

        <Text size="2" weight="medium">
          Headers (for all requests):
        </Text>
        <CopyableCode code={`Authorization: Bearer ${project.api_key}`} />

        <Heading size="5" weight="medium">
          Track Events
        </Heading>

        <Text size="2" color="gray">
          Create events for customers:
        </Text>

        <Text size="2" weight="medium">
          Endpoint:
        </Text>
        <CopyableCode
          code={`POST ${validatedProps.current_scheme}://${validatedProps.current_domain}/api/events`}
        />

        <Text size="2" weight="medium">
          Example payload:
        </Text>
        <CopyableCode code={exampleEventPayload} className="whitespace-pre-wrap" />

        <Text size="2" weight="medium">
          Required parameters:
        </Text>
        <Text size="2">
          • <Text weight="medium">email</Text>: Email address of the customer
        </Text>
        <Text size="2">
          • <Text weight="medium">name</Text>: Name of the event (e.g., &quot;signup&quot;,
          &quot;purchase&quot;, &quot;page_view&quot;)
        </Text>
        <Text size="2">
          • <Text weight="medium">custom_fields</Text>: Optional object with additional event data
        </Text>

        <Heading size="5" weight="medium">
          Update Customers
        </Heading>

        <Text size="2" color="gray">
          Update customer custom fields (new fields are merged with existing ones):
        </Text>

        <Text size="2" weight="medium">
          Endpoint:
        </Text>
        <CopyableCode
          code={`PUT ${validatedProps.current_scheme}://${validatedProps.current_domain}/api/customers/user@example.com`}
        />

        <Text size="2" weight="medium">
          Example payload:
        </Text>
        <CopyableCode code={exampleCustomerPayload} className="whitespace-pre-wrap" />

        <Text size="2" weight="medium">
          URL parameters:
        </Text>
        <Text size="2">
          • <Text weight="medium">email</Text>: Email address of the customer (in the URL path)
        </Text>
        <Text size="2" weight="medium">
          Optional parameters:
        </Text>
        <Text size="2">
          • <Text weight="medium">custom_fields</Text>: Object with customer data to merge with
          existing fields
        </Text>

        <Heading size="5" weight="medium">
          Register Devices
        </Heading>

        <Text size="2" color="gray">
          Register an iOS device token for a customer. Each call marks the device as active.
        </Text>

        <Text size="2" weight="medium">
          Endpoint:
        </Text>
        <CopyableCode
          code={`POST ${validatedProps.current_scheme}://${validatedProps.current_domain}/api/devices`}
        />

        <Text size="2" weight="medium">
          Example payload:
        </Text>
        <CopyableCode code={exampleDevicePayload} className="whitespace-pre-wrap" />

        <Text size="2" weight="medium">
          Required parameters:
        </Text>
        <Text size="2">
          • <Text weight="medium">email</Text>: Email address of the customer
        </Text>
        <Text size="2">
          • <Text weight="medium">apns_token</Text>: APNs device token (iOS)
        </Text>
      </Flex>
    </ProjectLayout>
  )
}
